/**
 * Normalize an arbitrary thrown value into the closed `LogError` shape.
 *
 * Its own module because two tiers need the SAME normalization and only one
 * of them has a `Logger`: the browser reaches it through `Logger.error()`,
 * while a Convex function builds a `BaseLogEntry` by hand and emits it on the
 * native log stream. Two normalizers would mean an `error.type` that means one
 * thing from the client and another from the backend — exactly the drift the
 * shared canonical event exists to prevent.
 *
 * `unknown` rather than `Error` is the honest parameter type: `catch` binds
 * `unknown` and `throw` accepts any value. Every arm must produce a `message`,
 * because the ingest schema requires one and an entry without it is dropped
 * whole rather than partially.
 */

import {
  MAX_ERROR_CODE_LENGTH,
  MAX_ERROR_MESSAGE_LENGTH,
  MAX_ERROR_TYPE_LENGTH,
} from "./ingest-limits";
import type { LogError } from "./types";

/**
 * Clamp a string to a published ingest limit, marking where it was cut.
 *
 * The ingest route validates every string on `error` against these lengths
 * and drops the *whole entry* when one is over — its schema is an
 * unauthenticated route's trust boundary, so it rejects rather than
 * repairs (see ingest-limits.ts). The caller sees a 204 either way and
 * `WorkerLogBackend` never reads `X-Log-Dropped`, so an over-long
 * `error.message` loses the entire event silently at both ends. Clamping
 * here is what turns that into a well-formed event that survives ingest.
 */
function clampToLimit(value: string, max: number): string {
  if (value.length <= max) return value;
  // Reserve the final code unit for the ellipsis, and never cut between a
  // surrogate pair: `error.message` is routinely built from uncontrolled
  // remote text, and a lone surrogate would survive into the JSON body.
  let end = max - 1;
  const lastKept = value.charCodeAt(end - 1);
  if (lastKept >= 0xd800 && lastKept <= 0xdbff) end -= 1;
  return `${value.slice(0, end)}…`;
}

/**
 * Coerce an error's `.code` into a schema-conforming string, or drop it.
 *
 * Some native error types carry a non-string `code` at runtime despite
 * TypeScript's `Error` type saying nothing about it — DOMException's legacy
 * numeric `.code` (e.g. `8` for `NOT_FOUND_ERR`) is the concrete case. Every
 * ingest path validates `LogError.code` as a string (see ingest.ts's Zod
 * schema), so an uncoerced number fails that check and silently drops the
 * whole event — never turn `code` into a value that can do that. The length
 * clamp is the same obligation: that schema bounds `code` as well as types
 * it, and a `code` over the bound drops the event exactly as a numeric one
 * would.
 */
function toStringCode(value: unknown): string | undefined {
  if (typeof value === "string") {
    return clampToLimit(value, MAX_ERROR_CODE_LENGTH);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return undefined;
}

/** Options for `toLogError`. Every one is opt-in; the default is the bare shape. */
export interface ToLogErrorOptions {
  /**
   * For an unexpected error: append its cause chain and stack frames to
   * `message` (see withStack). Opt-in because the normalizer is shared —
   * a Convex function's expected-failure events keep their bare message
   * unless that call site asks.
   */
  stack?: boolean;
}

/** How many `cause` links withStack follows before it stops. */
const MAX_CAUSE_DEPTH = 5;

/** A thrown non-Error as text: a primitive printed, an object by its tag. */
function describeThrown(value: unknown): string {
  switch (typeof value) {
    case "string":
      return value;
    case "number":
    case "boolean":
    case "bigint":
    case "symbol":
      return value.toString();
    case "undefined":
      return "undefined";
    default:
      return value === null ? "null" : Object.prototype.toString.call(value);
  }
}

/** `typeof` for a primitive; the constructor's name, if it has one, for an object. */
function kindOf(value: unknown): string {
  if (value === null) return "null";
  if (typeof value !== "object") return typeof value;
  const name: unknown = (value as { constructor?: { name?: unknown } })
    .constructor?.name;
  return typeof name === "string" && name !== "" ? name : "object";
}

/**
 * `message`, then the cause chain, then the stack frames — for an unexpected
 * error, the one kind logging-strategy §10 allows a stack for, and only
 * inside `error.message`, never as a key of its own. An unhandled error used
 * to reach the sink as `type` and `message` alone.
 *
 * The message comes first so the clamp that follows cuts frames, not it. A
 * cause is described as `Type: message` when it is an Error and by its tag
 * otherwise — never serialized, since a cause is often a third-party object
 * (§10) — and the chain stops at MAX_CAUSE_DEPTH, which is also what ends a
 * cycle. `cause` set to `null` ends the chain rather than describing a
 * cause of kind "null": `new Error(m, { cause: null })` is indistinguishable
 * from not passing `cause` at all, and printing one prints "Caused by:
 * null: null" for information that was never there.
 *
 * V8 repeats "Name: message" as the stack's first line(s) — plural, because
 * a multi-line `message` puts as many lines in the header as it has lines
 * of its own, and dropping only `frames[0]` leaves the header's remaining
 * lines behind as if they were frames, printing the message a second time.
 * `headerLines` is how many lines the header actually occupies, so the
 * whole thing is compared and dropped at once.
 */
function withStack(error: Error, message: string): string {
  const lines = [message];
  let cause: unknown = (error as { cause?: unknown }).cause;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && cause != null; depth++) {
    if (cause instanceof Error) {
      lines.push(`Caused by: ${cause.name}: ${cause.message}`);
      cause = (cause as { cause?: unknown }).cause;
    } else {
      lines.push(`Caused by: ${kindOf(cause)}: ${describeThrown(cause)}`);
      cause = undefined;
    }
  }
  if (typeof error.stack === "string") {
    const frames = error.stack.split("\n");
    const headerLines = message.split("\n").length;
    const header = frames.slice(0, headerLines).join("\n");
    if (header === `${error.name}: ${message}`) {
      frames.splice(0, headerLines);
    } else if (frames[0] === error.name) {
      frames.shift();
    }
    if (frames.length > 0) lines.push(frames.join("\n"));
  }
  return lines.join("\n");
}

/**
 * Does this value carry the two fields a `LogError` must have?
 *
 * A structural check rather than an `instanceof`: the shape is a plain
 * object contract (logging-strategy §10), and callers legitimately build one
 * by hand. `code` is not checked — it is optional, and `toStringCode` coerces
 * whatever is there.
 */
function isLogErrorShaped(value: unknown): value is LogError {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { type?: unknown; message?: unknown };
  return (
    typeof candidate.type === "string" && typeof candidate.message === "string"
  );
}

/** What toLogError records for a thrown value nothing can be read from. */
const UNREADABLE_ERROR: LogError = {
  type: "UnknownError",
  message: "<unreadable thrown value>",
};

/**
 * withStack, or the bare message if reading the stack or a cause throws: the
 * message was already read, and a hostile getter on `stack` or `cause` (or a
 * revoked proxy as the cause) should cost the frames, not the event's one
 * readable fact.
 */
function withStackOrBare(error: Error, message: string): string {
  try {
    return withStack(error, message);
  } catch {
    return message;
  }
}

/**
 * Normalize any thrown value to the LogError structure, and never throw.
 *
 * Reading the value can itself throw: a revoked Proxy (an immer draft that
 * escaped its producer — `instanceof` and `String()` both throw on one), an
 * Error whose `message` or `name` getter throws, an object whose
 * `Symbol.toStringTag` getter throws, a `{ type, message }`-shaped object
 * whose `type` getter throws. Every caller is reporting a failure already —
 * Logger.error, and convex-retells' logEvent on its error paths — and must
 * not become a second one, so such a value is recorded as
 * UNREADABLE_ERROR. That changes nothing for a value that could be read.
 */
export function toLogError(
  error: unknown,
  options: ToLogErrorOptions = {},
): LogError {
  try {
    return readLogError(error, options);
  } catch {
    return UNREADABLE_ERROR;
  }
}

/**
 * The normalization itself, free to throw on a hostile value (see
 * toLogError).
 *
 * The discriminator is `instanceof Error`, not the presence of a `type`
 * key. A duck-typed `"type" in error && "message" in error` cannot tell
 * the two apart: `message` lives on `Error.prototype`, so it is `in`
 * every Error, and the test collapses to "does this value carry a `type`
 * property" — which `class FetchError extends Error { type = "system" }`
 * (the node-fetch idiom, echoed by many client SDKs) satisfies. Such an
 * error took the LogError arm and was spread, and `{ ...error }` does
 * not copy `message`: it is an own property but a non-enumerable one.
 * The resulting LogError had no `message` at all, which the ingest
 * schema requires, so the entire event was dropped at ingest and the
 * console backend printed a messageless error.
 */
function readLogError(error: unknown, options: ToLogErrorOptions): LogError {
  if (error instanceof Error) {
    // `code` is coerced rather than cast: some DOM error types (e.g.
    // DOMException) still expose a legacy *numeric* `.code` alongside
    // `.name`. The ingest Zod schema requires `error.code` to be a
    // string; an uncoerced number fails that check and the whole entry
    // — including the unhandled-error event this exists to capture — is
    // silently dropped by partitionLogEntries.
    return {
      type: clampToLimit(error.name ?? "Error", MAX_ERROR_TYPE_LENGTH),
      message: clampToLimit(
        options.stack === true
          ? withStackOrBare(error, error.message)
          : error.message,
        MAX_ERROR_MESSAGE_LENGTH,
      ),
      // Clamped inside toStringCode, which owns every rule `code` has
      // to satisfy to survive the schema.
      code: toStringCode((error as Error & { code?: unknown }).code),
    };
  }

  // `catch` binds `unknown`, and `throw` accepts any value, so the two
  // arms below are reachable from ordinary call sites — not defensive
  // padding. A thrown string is the common one (`throw "boom"`, and
  // `window.onerror`'s `message` when no Error was constructed).
  if (typeof error === "string") {
    return {
      type: "Error",
      message: clampToLimit(error, MAX_ERROR_MESSAGE_LENGTH),
    };
  }

  if (isLogErrorShaped(error)) {
    // The three keys are picked explicitly rather than spread: a caller can
    // hand this any object shaped like one, and `LogError` is a closed
    // three-key shape (logging-strategy §10), so a spread would both leak
    // extra keys in and drop any non-enumerable own property back out.
    // `code` is still coerced for the same reason as above.
    return {
      type: clampToLimit(error.type, MAX_ERROR_TYPE_LENGTH),
      message: clampToLimit(error.message, MAX_ERROR_MESSAGE_LENGTH),
      code: toStringCode(error.code),
    };
  }

  // Anything else — a number, null, a plain object. `String(error)` is the
  // only honest rendering, and a distinct `type` keeps it greppable rather
  // than pretending it was an Error.
  return {
    type: "UnknownError",
    message: clampToLimit(String(error), MAX_ERROR_MESSAGE_LENGTH),
  };
}
